// src/components/modal/Modal.tsx

import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { ModalProps } from './types.ts';
import { ModalContext } from './ModalContext.ts';

import ModalHeader from './ModalHeader.tsx';
import ModalTitle from './ModalTitle.tsx';
import ModalBody from './ModalBody.tsx';
import ModalFooter from './ModalFooter.tsx';

function Modal({
    show = false,
    onHide,
    children,

    size,
    centered,
    scrollable,

    backdrop = true,
    closeOnBackdrop = true,

    className = '',
    dialogClassName = '',
    contentClassName = '',
}: ModalProps) {
    useEffect(() => {
        if (!show) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onHide?.();
            }
        };

        document.addEventListener('keydown', handleEscape);

        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [show, onHide]);

    if (!show) return null;

    const sizeClass = {
        sm: 'max-w-sm',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
    }[size as keyof typeof size] ?? 'max-w-2xl';

    return createPortal(
            <div
                className={`fixed inset-0 z-50 flex justify-center
                ${centered ? 'items-center' : 'items-start pt-10'}
                ${className}
            `}
            >
                {/* Backdrop */}
                {backdrop && (
                    <div
                        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                        onClick={() => {
                            if (closeOnBackdrop) {
                                onHide?.();
                            }
                        }}
                    />
                )}

                {/* Dialog */}
                <div
                    className={`relative z-10 w-full mx-4
                    ${sizeClass}
                    ${dialogClassName}
                `}
                >
                    {/* Content */}
                    <div className={`bg-white rounded-lg shadow-2xl overflow-hidden
                        ${scrollable ? 'max-h-[90vh] overflow-y-auto' : ''}
                        ${contentClassName}
                    `}
                    >
                        <ModalContext.Provider
                            value={{
                                onClose: onHide,
                            }}
                        >
                            {children}
                        </ModalContext.Provider>
                    </div>
                </div>
            </div>,
        document.body
    );
}

Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;